/**
 * Hero — top-of-page banner with eyebrow, big title, subtitle, two CTAs,
 * and an optional metrics strip.  Background uses the configurable
 * theme.heroGradient.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { COLOR, FONT, RADIUS, ELEVATION } from '../../../components/ss/tokens';
import useSiteConfig from '../useSiteConfig';

function isExternal(href) {
  return /^(https?:|mailto:|tel:)/i.test(href || '');
}

function CTA({ link, variant }) {
  if (!link?.href) return null;
  const baseStyle = {
    padding: '14px 26px',
    borderRadius: RADIUS.pill,
    fontSize: FONT.size.md,
    fontWeight: FONT.weight.semi,
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'transform .12s, box-shadow .12s',
    boxShadow: variant === 'primary' ? ELEVATION.md : 'none',
  };
  const styles = variant === 'primary'
    ? { ...baseStyle, background: '#fff', color: COLOR.text, border: 'none' }
    : { ...baseStyle, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' };
  const cls = variant === 'primary' ? 'hhms-cta-shine' : undefined;
  if (isExternal(link.href) || link.href.includes('#')) {
    return <a href={link.href} className={cls} style={styles}>{link.label}</a>;
  }
  return <Link to={link.href} className={cls} style={styles}>{link.label}</Link>;
}

export default function Hero() {
  const { config } = useSiteConfig();
  const { hero, theme } = config;
  if (!hero) return null;

  return (
    <section
      className="hhms-hero-section"
      style={{
        background: theme?.heroGradient || 'linear-gradient(135deg, #4a1228 0%, #7b1e3c 100%)',
        backgroundSize: '180% 180%',
        animation: 'hhmsGradient 18s ease infinite',
        color: '#fff',
        padding: '128px 40px 116px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Floating orbs */}
      <div aria-hidden className="hhms-float" style={{ position:'absolute', top:'-120px', right:'-80px', width:380, height:380, borderRadius:'50%', background:'radial-gradient(circle, rgba(79,195,247,0.30), transparent 70%)', animation:'hhmsFloat 14s ease-in-out infinite', pointerEvents:'none' }} />
      <div aria-hidden className="hhms-float2" style={{ position:'absolute', bottom:'-140px', left:'-100px', width:440, height:440, borderRadius:'50%', background:'radial-gradient(circle, rgba(0,137,123,0.26), transparent 70%)', animation:'hhmsFloat2 17s ease-in-out infinite', pointerEvents:'none' }} />
      {/* Decorative radial glow */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 78% 18%, rgba(79,195,247,0.28) 0%, transparent 55%), ' +
            'radial-gradient(circle at 8% 86%, rgba(0,137,123,0.22) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          maxWidth: 1440,
          margin: '0 auto',
          position: 'relative',
          textAlign: 'left',
        }}
      >
        {hero.eyebrow && (
          <div
            style={{
              display: 'inline-block',
              padding: '6px 14px',
              borderRadius: RADIUS.pill,
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.22)',
              fontSize: FONT.size.sm,
              fontWeight: FONT.weight.semi,
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              marginBottom: 24,
            }}
            className="hhms-anim-up"
          >
            {hero.eyebrow}
          </div>
        )}

        <h1
          style={{
            margin: 0,
            fontSize: 64,
            lineHeight: 1.06,
            fontWeight: FONT.weight.xbold,
            letterSpacing: '-1.2px',
            maxWidth: 720,
            ['--d']: '90ms',
          }}
          className="hhms-hero-title hhms-anim-up"
        >
          {hero.title}
        </h1>

        {hero.subtitle && (
          <p
            style={{
              marginTop: 24,
              fontSize: 20,
              lineHeight: 1.55,
              color: 'rgba(255,255,255,0.86)',
              maxWidth: 620,
              ['--d']: '180ms',
            }}
            className="hhms-anim-up"
          >
            {hero.subtitle}
          </p>
        )}

        <div
          style={{
            marginTop: 36,
            display: 'flex',
            gap: 14,
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            ['--d']: '270ms',
          }}
          className="hhms-anim-up"
        >
          {hero.ctaPrimary   && <CTA link={hero.ctaPrimary}   variant="primary"   />}
          {hero.ctaSecondary && <CTA link={hero.ctaSecondary} variant="secondary" />}
        </div>

        {hero.metrics?.length > 0 && (
          <div
            style={{
              marginTop: 80,
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(hero.metrics.length, 4)}, 1fr)`,
              gap: 32,
              maxWidth: '100%',
              maxWidth: 760,
              padding: '28px 0',
              borderTop: '1px solid rgba(255,255,255,0.18)',
              borderBottom: '1px solid rgba(255,255,255,0.18)',
            }}
            className="hhms-hero-metrics hhms-anim-up"
          >
            {hero.metrics.map((m, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div
                  data-countup={m.value}
                  style={{
                    fontSize: 34,
                    fontWeight: FONT.weight.xbold,
                    letterSpacing: '-0.5px',
                  }}
                >
                  {m.value}
                </div>
                <div
                  style={{
                    fontSize: FONT.size.sm,
                    color: 'rgba(255,255,255,0.78)',
                    textTransform: 'uppercase',
                    letterSpacing: '1.2px',
                    marginTop: 6,
                  }}
                >
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 720px) {
          .hhms-hero-title { font-size: 40px !important; letter-spacing: -0.5px !important; }
          .hhms-hero-metrics { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 640px) {
          .hhms-hero-section { padding-left: 20px !important; padding-right: 20px !important; }
        }
      `}</style>
    </section>
  );
}
